import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "space-y-6" }
const _hoisted_2 = { class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4" }
const _hoisted_3 = { class: "flex flex-wrap gap-6 mt-4" }
const _hoisted_4 = { class: "flex items-center gap-2 cursor-pointer" }
const _hoisted_5 = { class: "flex items-center gap-2 cursor-pointer" }
const _hoisted_6 = { class: "flex items-center gap-2 cursor-pointer" }
const _hoisted_7 = { class: "flex flex-wrap gap-6 mb-4" }
const _hoisted_8 = { class: "flex items-center gap-2 cursor-pointer" }
const _hoisted_9 = { class: "flex items-center gap-2 cursor-pointer" }
const _hoisted_10 = ["value"]
const _hoisted_11 = { class: "mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-dashed border-gray-300 dark:border-gray-600" }
const _hoisted_12 = {
  key: 0,
  class: "mt-3 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700"
}
const _hoisted_13 = { class: "space-y-1 max-h-40 overflow-y-auto mb-3" }
const _hoisted_14 = { class: "space-y-3" }
const _hoisted_15 = { class: "flex flex-wrap gap-3 items-end" }
const _hoisted_16 = { class: "flex-1 min-w-[120px]" }
const _hoisted_17 = ["onUpdate:modelValue"]
const _hoisted_18 = { class: "flex-[2] min-w-[200px] relative" }
const _hoisted_19 = ["onUpdate:modelValue", "onInput"]
const _hoisted_20 = { class: "flex-1 min-w-[150px]" }
const _hoisted_21 = ["onUpdate:modelValue"]
const _hoisted_22 = ["onClick"]
const _hoisted_23 = { class: "mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-dashed border-gray-300 dark:border-gray-600" }
const _hoisted_24 = {
  key: 0,
  class: "mt-3 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700"
}
const _hoisted_25 = { class: "space-y-1 max-h-40 overflow-y-auto mb-3" }
const _hoisted_26 = { class: "space-y-3" }
const _hoisted_27 = { class: "flex flex-wrap gap-3 items-end" }
const _hoisted_28 = { class: "flex-1 min-w-[150px]" }
const _hoisted_29 = ["onUpdate:modelValue"]
const _hoisted_30 = { class: "flex-[2] min-w-[200px] relative" }
const _hoisted_31 = ["onUpdate:modelValue", "onInput"]
const _hoisted_32 = { class: "flex-1 min-w-[150px]" }
const _hoisted_33 = ["onUpdate:modelValue"]
const _hoisted_34 = ["onClick"]
const _hoisted_35 = ["value"]

import { ref, computed, inject, watch } from 'vue'
import { useTheme } from '../../../utils/useTheme'
import { taskSentinelService } from '../../../services/task-sentinel'
import type { Team, Manager, Developer, BulkEmailResult } from '../../../types/task-sentinel'


export default /*@__PURE__*/_defineComponent({
  __name: 'TeamForm',
  props: {
    team: {}
  },
  emits: ["update:team", "lookup-email"],
  setup(__props: any, { emit: __emit }) {

const theme = inject('theme', useTheme())

const props = __props

const emit = __emit

// Local copy of team for editing
const localTeam = ref<Team>({
  id: '',
  name: '',
  deleted: false,
  managers: [],
  devs: [],
  meta: {
    isCombinedFolder: false,
    skipPreviousQuarter: false,
    subModules: []
  }
})

// Bulk add state
const bulkManagerEmails = ref('')
const bulkDevEmails = ref('')
const bulkManagerResults = ref<BulkEmailResult[]>([])
const bulkDevResults = ref<BulkEmailResult[]>([])

// Debounce timers
let emailLookupTimer: ReturnType<typeof setTimeout> | null = null
let bulkLookupTimer: ReturnType<typeof setTimeout> | null = null

// Computed
const subModulesString = computed(() => {
  return localTeam.value.meta?.subModules?.join(', ') || ''
})

const ignoreDevsString = computed(() => {
  return localTeam.value.ignoreDevs?.join(', ') || ''
})

// Flag to prevent circular updates
let isUpdatingFromProp = false

// Watch for prop changes - only update if significantly different
watch(
  () => props.team,
  (newTeam) => {
    if (newTeam && !isUpdatingFromProp) {
      const newTeamStr = JSON.stringify(newTeam)
      const localTeamStr = JSON.stringify(localTeam.value)

      // Only update if actually different
      if (newTeamStr !== localTeamStr) {
        isUpdatingFromProp = true
        localTeam.value = JSON.parse(newTeamStr)
        // Ensure meta exists
        if (!localTeam.value.meta) {
          localTeam.value.meta = {
            isCombinedFolder: false,
            skipPreviousQuarter: false,
            subModules: []
          }
        }
        // Reset flag after next tick
        setTimeout(() => {
          isUpdatingFromProp = false
        }, 0)
      }
    }
  },
  { immediate: true, deep: true }
)

// Watch for local changes and emit - skip if update came from prop
watch(
  localTeam,
  (newValue) => {
    if (!isUpdatingFromProp) {
      emit('update:team', JSON.parse(JSON.stringify(newValue)))
    }
  },
  { deep: true }
)

// Methods
const updateSubModules = (value: string) => {
  localTeam.value.meta!.subModules = value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s)
}

const updateIgnoreDevs = (value: string) => {
  localTeam.value.ignoreDevs = value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s)
}

const addManager = () => {
  localTeam.value.managers.push({
    type: '',
    emailPrefix: '',
    slackId: ''
  })
}

const removeManager = (index: number) => {
  localTeam.value.managers.splice(index, 1)
}

const addDeveloper = () => {
  localTeam.value.devs.push({
    name: '',
    emailPrefix: '',
    slackId: ''
  })
}

const removeDeveloper = (index: number) => {
  localTeam.value.devs.splice(index, 1)
}

const debouncedEmailLookup = (value: string, type: 'manager' | 'dev', index: number) => {
  if (emailLookupTimer) {
    clearTimeout(emailLookupTimer)
  }

  if (value.includes('@')) {
    emailLookupTimer = setTimeout(() => {
      emit('lookup-email', value, type, index)
    }, 800)
  }
}

const debouncedBulkLookup = (type: 'manager' | 'dev') => {
  if (bulkLookupTimer) {
    clearTimeout(bulkLookupTimer)
  }

  const emailsText = type === 'manager' ? bulkManagerEmails.value : bulkDevEmails.value
  const emails = emailsText
    .split(',')
    .map((e) => e.trim())
    .filter((e) => e && e.includes('@'))

  if (emails.length === 0) {
    if (type === 'manager') {
      bulkManagerResults.value = []
    } else {
      bulkDevResults.value = []
    }
    return
  }

  bulkLookupTimer = setTimeout(async () => {
    const results: BulkEmailResult[] = []

    for (const email of emails) {
      try {
        const response = await taskSentinelService.lookupUserByEmail(email)
        if (response.success && response.data) {
          results.push({
            email,
            emailPrefix: taskSentinelService.extractEmailPrefix(response.data.email),
            slackId: response.data.slackId || '',
            name: response.data.name,
            found: true
          })
        } else {
          results.push({
            email,
            emailPrefix: '',
            slackId: '',
            found: false
          })
        }
      } catch {
        results.push({
          email,
          emailPrefix: '',
          slackId: '',
          found: false
        })
      }
    }

    if (type === 'manager') {
      bulkManagerResults.value = results
    } else {
      bulkDevResults.value = results
    }
  }, 1000)
}

const applyBulkManagers = () => {
  const foundResults = bulkManagerResults.value.filter((r) => r.found)
  for (const result of foundResults) {
    localTeam.value.managers.push({
      type: '',
      emailPrefix: result.emailPrefix,
      slackId: result.slackId
    })
  }
  bulkManagerEmails.value = ''
  bulkManagerResults.value = []
}

const dismissBulkManagers = () => {
  bulkManagerEmails.value = ''
  bulkManagerResults.value = []
}

const applyBulkDevelopers = () => {
  const foundResults = bulkDevResults.value.filter((r) => r.found)
  for (const result of foundResults) {
    localTeam.value.devs.push({
      name: result.name || '',
      emailPrefix: result.emailPrefix,
      slackId: result.slackId
    })
  }
  bulkDevEmails.value = ''
  bulkDevResults.value = []
}

const dismissBulkDevelopers = () => {
  bulkDevEmails.value = ''
  bulkDevResults.value = []
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass([
        _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border rounded-lg p-6'
      ])
    }, [
      _cache[24] || (_cache[24] = _createElementVNode("h2", { class: "text-lg font-bold text-blue-500 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700" }, " Basic Information ", -1)),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", null, [
          _cache[16] || (_cache[16] = _createElementVNode("label", { class: "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5" }, " Team Name ", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((localTeam.value.name) = $event)),
            type: "text",
            class: "w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          }, null, 512), [
            [_vModelText, localTeam.value.name]
          ])
        ]),
        _createElementVNode("div", null, [
          _cache[17] || (_cache[17] = _createElementVNode("label", { class: "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5" }, " Folder ID ", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((localTeam.value.standupId) = $event)),
            type: "text",
            class: "w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          }, null, 512), [
            [_vModelText, localTeam.value.standupId]
          ])
        ]),
        _createElementVNode("div", null, [
          _cache[18] || (_cache[18] = _createElementVNode("label", { class: "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5" }, " Roadmap Board ID ", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((localTeam.value.roadmapBoardId) = $event)),
            type: "text",
            class: "w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          }, null, 512), [
            [_vModelText, localTeam.value.roadmapBoardId]
          ])
        ]),
        _createElementVNode("div", null, [
          _cache[19] || (_cache[19] = _createElementVNode("label", { class: "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5" }, " Parent Group ", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((localTeam.value.parentGroup) = $event)),
            type: "text",
            class: "w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          }, null, 512), [
            [_vModelText, localTeam.value.parentGroup]
          ])
        ]),
        _createElementVNode("div", null, [
          _cache[20] || (_cache[20] = _createElementVNode("label", { class: "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5" }, " Type ", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((localTeam.value.type) = $event)),
            type: "text",
            class: "w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          }, null, 512), [
            [_vModelText, localTeam.value.type]
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("label", _hoisted_4, [
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((localTeam.value.deleted) = $event)),
            type: "checkbox",
            class: "w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
          }, null, 512), [
            [_vModelCheckbox, localTeam.value.deleted]
          ]),
          _cache[21] || (_cache[21] = _createElementVNode("span", { class: "text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400" }, "Deleted", -1))
        ]),
        _createElementVNode("label", _hoisted_5, [
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((localTeam.value.isNew) = $event)),
            type: "checkbox",
            class: "w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
          }, null, 512), [
            [_vModelCheckbox, localTeam.value.isNew]
          ]),
          _cache[22] || (_cache[22] = _createElementVNode("span", { class: "text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400" }, "Is New", -1))
        ]),
        _createElementVNode("label", _hoisted_6, [
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((localTeam.value.sendToEmOnly) = $event)),
            type: "checkbox",
            class: "w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
          }, null, 512), [
            [_vModelCheckbox, localTeam.value.sendToEmOnly]
          ]),
          _cache[23] || (_cache[23] = _createElementVNode("span", { class: "text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400" }, "Send to EM Only", -1))
        ])
      ])
    ], 2),
    _createElementVNode("div", {
      class: _normalizeClass([
        _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border rounded-lg p-6'
      ])
    }, [
      _cache[28] || (_cache[28] = _createElementVNode("h2", { class: "text-lg font-bold text-blue-500 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700" }, " Meta Information ", -1)),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("label", _hoisted_8, [
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((localTeam.value.meta!.isCombinedFolder) = $event)),
            type: "checkbox",
            class: "w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
          }, null, 512), [
            [_vModelCheckbox, localTeam.value.meta!.isCombinedFolder]
          ]),
          _cache[25] || (_cache[25] = _createElementVNode("span", { class: "text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400" }, "Is Combined Folder", -1))
        ]),
        _createElementVNode("label", _hoisted_9, [
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((localTeam.value.meta!.skipPreviousQuarter) = $event)),
            type: "checkbox",
            class: "w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
          }, null, 512), [
            [_vModelCheckbox, localTeam.value.meta!.skipPreviousQuarter]
          ]),
          _cache[26] || (_cache[26] = _createElementVNode("span", { class: "text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400" }, "Skip Previous Quarter", -1))
        ])
      ]),
      _createElementVNode("div", null, [
        _cache[27] || (_cache[27] = _createElementVNode("label", { class: "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5" }, " Sub Modules (comma-separated) ", -1)),
        _createElementVNode("input", {
          value: subModulesString.value,
          type: "text",
          placeholder: "Module1, Module2, Module3",
          class: "w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white",
          onInput: _cache[10] || (_cache[10] = ($event: any) => (updateSubModules(($event.target as HTMLInputElement).value)))
        }, null, 40, _hoisted_10)
      ])
    ], 2),
    _createElementVNode("div", {
      class: _normalizeClass([
        _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border rounded-lg p-6'
      ])
    }, [
      _cache[34] || (_cache[34] = _createElementVNode("h2", { class: "text-lg font-bold text-blue-500 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700" }, " Managers ", -1)),
      _createElementVNode("div", _hoisted_11, [
        _cache[30] || (_cache[30] = _createElementVNode("label", { class: "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5" }, " Bulk Add Managers ", -1)),
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((bulkManagerEmails).value = $event)),
          type: "text",
          placeholder: "Paste comma-separated emails (e.g., email1@example.com, email2@example.com)",
          class: "w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white",
          onInput: _cache[12] || (_cache[12] = ($event: any) => (debouncedBulkLookup('manager')))
        }, null, 544), [
          [_vModelText, bulkManagerEmails.value]
        ]),
        (bulkManagerResults.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
              _cache[29] || (_cache[29] = _createElementVNode("div", { class: "text-xs font-semibold uppercase text-blue-600 dark:text-blue-400 mb-2" }, " Found in Slack ", -1)),
              _createElementVNode("div", _hoisted_13, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bulkManagerResults.value, (result) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: result.email,
                    class: _normalizeClass([
                'text-sm p-2 rounded',
                result.found
                  ? 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  : 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
              ])
                  }, [
                    _createElementVNode("strong", null, _toDisplayString(result.email), 1),
                    _createTextVNode(" " + _toDisplayString(result.found
                  ? `→ ${result.emailPrefix}, ${result.slackId || 'No Slack ID'}`
                  : '→ Not found'), 1)
                  ], 2))
                }), 128))
              ]),
              _createElementVNode("div", { class: "flex gap-2" }, [
                _createElementVNode("button", {
                  class: "px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm rounded font-medium",
                  onClick: applyBulkManagers
                }, " Apply All "),
                _createElementVNode("button", {
                  class: "px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded font-medium",
                  onClick: dismissBulkManagers
                }, " Dismiss ")
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_14, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localTeam.value.managers, (manager, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: index,
            class: "p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
          }, [
            _createElementVNode("div", _hoisted_15, [
              _createElementVNode("div", _hoisted_16, [
                _cache[31] || (_cache[31] = _createElementVNode("label", { class: "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1" }, "Type", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": ($event: any) => ((manager.type) = $event),
                  type: "text",
                  placeholder: "DIRECTOR, EM, TL, PM, GPM",
                  class: "w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                }, null, 8, _hoisted_17), [
                  [_vModelText, manager.type]
                ])
              ]),
              _createElementVNode("div", _hoisted_18, [
                _cache[32] || (_cache[32] = _createElementVNode("label", { class: "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1" }, "Email Prefix", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": ($event: any) => ((manager.emailPrefix) = $event),
                  type: "text",
                  placeholder: "Enter email or prefix",
                  class: "w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white",
                  onInput: ($event: any) => (debouncedEmailLookup(manager.emailPrefix, 'manager', index))
                }, null, 40, _hoisted_19), [
                  [_vModelText, manager.emailPrefix]
                ])
              ]),
              _createElementVNode("div", _hoisted_20, [
                _cache[33] || (_cache[33] = _createElementVNode("label", { class: "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1" }, "Slack ID", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": ($event: any) => ((manager.slackId) = $event),
                  type: "text",
                  class: "w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                }, null, 8, _hoisted_21), [
                  [_vModelText, manager.slackId]
                ])
              ]),
              _createElementVNode("button", {
                class: "px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-sm rounded font-medium",
                onClick: ($event: any) => (removeManager(index))
              }, " Remove ", 8, _hoisted_22)
            ])
          ]))
        }), 128))
      ]),
      _createElementVNode("button", {
        class: "mt-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium text-sm",
        onClick: addManager
      }, " + Add Manager ")
    ], 2),
    _createElementVNode("div", {
      class: _normalizeClass([
        _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border rounded-lg p-6'
      ])
    }, [
      _cache[40] || (_cache[40] = _createElementVNode("h2", { class: "text-lg font-bold text-blue-500 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700" }, " Developers ", -1)),
      _createElementVNode("div", _hoisted_23, [
        _cache[36] || (_cache[36] = _createElementVNode("label", { class: "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5" }, " Bulk Add Developers ", -1)),
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((bulkDevEmails).value = $event)),
          type: "text",
          placeholder: "Paste comma-separated emails (e.g., email1@example.com, email2@example.com)",
          class: "w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white",
          onInput: _cache[14] || (_cache[14] = ($event: any) => (debouncedBulkLookup('dev')))
        }, null, 544), [
          [_vModelText, bulkDevEmails.value]
        ]),
        (bulkDevResults.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
              _cache[35] || (_cache[35] = _createElementVNode("div", { class: "text-xs font-semibold uppercase text-blue-600 dark:text-blue-400 mb-2" }, " Found in Slack ", -1)),
              _createElementVNode("div", _hoisted_25, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bulkDevResults.value, (result) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: result.email,
                    class: _normalizeClass([
                'text-sm p-2 rounded',
                result.found
                  ? 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  : 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
              ])
                  }, [
                    _createElementVNode("strong", null, _toDisplayString(result.email), 1),
                    _createTextVNode(" " + _toDisplayString(result.found
                  ? `→ ${result.name || 'N/A'}, ${result.emailPrefix}, ${result.slackId || 'No Slack ID'}`
                  : '→ Not found'), 1)
                  ], 2))
                }), 128))
              ]),
              _createElementVNode("div", { class: "flex gap-2" }, [
                _createElementVNode("button", {
                  class: "px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm rounded font-medium",
                  onClick: applyBulkDevelopers
                }, " Apply All "),
                _createElementVNode("button", {
                  class: "px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded font-medium",
                  onClick: dismissBulkDevelopers
                }, " Dismiss ")
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_26, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localTeam.value.devs, (dev, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: index,
            class: "p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
          }, [
            _createElementVNode("div", _hoisted_27, [
              _createElementVNode("div", _hoisted_28, [
                _cache[37] || (_cache[37] = _createElementVNode("label", { class: "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1" }, "Name", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": ($event: any) => ((dev.name) = $event),
                  type: "text",
                  class: "w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                }, null, 8, _hoisted_29), [
                  [_vModelText, dev.name]
                ])
              ]),
              _createElementVNode("div", _hoisted_30, [
                _cache[38] || (_cache[38] = _createElementVNode("label", { class: "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1" }, "Email Prefix", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": ($event: any) => ((dev.emailPrefix) = $event),
                  type: "text",
                  placeholder: "Enter email or prefix",
                  class: "w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white",
                  onInput: ($event: any) => (debouncedEmailLookup(dev.emailPrefix, 'dev', index))
                }, null, 40, _hoisted_31), [
                  [_vModelText, dev.emailPrefix]
                ])
              ]),
              _createElementVNode("div", _hoisted_32, [
                _cache[39] || (_cache[39] = _createElementVNode("label", { class: "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1" }, "Slack ID", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": ($event: any) => ((dev.slackId) = $event),
                  type: "text",
                  class: "w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                }, null, 8, _hoisted_33), [
                  [_vModelText, dev.slackId]
                ])
              ]),
              _createElementVNode("button", {
                class: "px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-sm rounded font-medium",
                onClick: ($event: any) => (removeDeveloper(index))
              }, " Remove ", 8, _hoisted_34)
            ])
          ]))
        }), 128))
      ]),
      _createElementVNode("button", {
        class: "mt-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium text-sm",
        onClick: addDeveloper
      }, " + Add Developer ")
    ], 2),
    _createElementVNode("div", {
      class: _normalizeClass([
        _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border rounded-lg p-6'
      ])
    }, [
      _cache[42] || (_cache[42] = _createElementVNode("h2", { class: "text-lg font-bold text-blue-500 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700" }, " Additional Information ", -1)),
      _createElementVNode("div", null, [
        _cache[41] || (_cache[41] = _createElementVNode("label", { class: "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5" }, " Ignore Devs (comma-separated email prefixes) ", -1)),
        _createElementVNode("input", {
          value: ignoreDevsString.value,
          type: "text",
          placeholder: "prefix1, prefix2, prefix3",
          class: "w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white",
          onInput: _cache[15] || (_cache[15] = ($event: any) => (updateIgnoreDevs(($event.target as HTMLInputElement).value)))
        }, null, 40, _hoisted_35)
      ])
    ], 2)
  ]))
}
}

})